import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/cartpage.scss";

const commerceConfig = siteConfig.commerce || {};
const shippingConfig = siteConfig.shipping || commerceConfig.shipping || {};

const toPositiveNumber = (value, fallback) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue >= 0
    ? parsedValue
    : fallback;
};

const SHIPPING_RATE = toPositiveNumber(
  shippingConfig.usRate ??
    shippingConfig.us?.rate ??
    shippingConfig.cost ??
    commerceConfig.shippingCost,
  15
);
const FREE_SHIPPING_THRESHOLD = toPositiveNumber(
  shippingConfig.freeShippingThreshold ??
    shippingConfig.freeThreshold ??
    commerceConfig.freeShippingThreshold,
  250
);
const PROMO_CODE = String(
  commerceConfig.discountCode || commerceConfig.promoCode || "CHANGE10"
).toUpperCase();
const configuredDiscount = toPositiveNumber(
  commerceConfig.discountRate ?? commerceConfig.discounts?.[PROMO_CODE],
  0.1
);
const PROMO_RATE = configuredDiscount > 1
  ? configuredDiscount / 100
  : configuredDiscount;

const currencyFormatter = new Intl.NumberFormat(siteConfig.locale || "en-US", {
  style: "currency",
  currency: siteConfig.currency?.code || "USD",
});

const formatMoney = (value) => currencyFormatter.format(value);

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);
  const [discountInput, setDiscountInput] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState("");
  const [discountMessage, setDiscountMessage] = useState("");

  const resolvedItems = useMemo(
    () =>
      cartItems.reduce((items, cartItem) => {
        const product = productData.find(
          (candidate) => String(candidate.id) === String(cartItem.id)
        );

        if (!product) return items;

        const quantity = Math.max(1, Number(cartItem.quantity) || 1);
        return [...items, { ...cartItem, quantity, product }];
      }, []),
    [cartItems]
  );

  const subtotal = resolvedItems.reduce(
    (total, item) => total + Number(item.product.price) * item.quantity,
    0
  );
  const discount = appliedDiscount === PROMO_CODE ? subtotal * PROMO_RATE : 0;
  const discountedSubtotal = Math.max(0, subtotal - discount);
  const hasFreeShipping =
    FREE_SHIPPING_THRESHOLD === 0 ||
    discountedSubtotal >= FREE_SHIPPING_THRESHOLD;
  const shipping = hasFreeShipping ? 0 : SHIPPING_RATE;
  const total = discountedSubtotal + shipping;
  const remainingForFreeShipping = Math.max(
    0,
    FREE_SHIPPING_THRESHOLD - discountedSubtotal
  );
  const shippingProgress = FREE_SHIPPING_THRESHOLD > 0
    ? Math.min(100, (discountedSubtotal / FREE_SHIPPING_THRESHOLD) * 100)
    : 100;
  const itemCount = resolvedItems.reduce(
    (count, item) => count + item.quantity,
    0
  );

  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleDiscountSubmit = (event) => {
    event.preventDefault();
    const normalizedCode = discountInput.trim().toUpperCase();

    if (!normalizedCode) {
      setDiscountMessage("Enter a discount code.");
      return;
    }

    if (normalizedCode !== PROMO_CODE) {
      setAppliedDiscount("");
      setDiscountMessage(`This code is not valid. Try ${PROMO_CODE}.`);
      return;
    }

    setAppliedDiscount(PROMO_CODE);
    setDiscountInput(PROMO_CODE);
    setDiscountMessage(
      `${PROMO_CODE} applied — you saved ${Math.round(PROMO_RATE * 100)}%.`
    );
  };

  const removeDiscount = () => {
    setAppliedDiscount("");
    setDiscountInput("");
    setDiscountMessage("Discount removed.");
  };

  const handleCheckout = () => {
    if (!resolvedItems.length) return;

    navigate("/checkout", {
      state: { discountCode: appliedDiscount },
    });
    scrollToTop();
  };

  if (!resolvedItems.length) {
    return (
      <main className="cart-page cart-page--empty" id="main-content">
        <section
          className="cart-page__empty"
          aria-labelledby="empty-cart-heading"
        >
          <p className="cart-page__eyebrow">Shopping bag</p>
          <h1 id="empty-cart-heading">Your cart is currently empty</h1>
          <p>
            Discover considered denim and everyday essentials made for repeat
            wear.
          </p>
          <Link
            to="/shop"
            onClick={scrollToTop}
            className="cart-page__primary-button"
          >
            Continue shopping
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page" id="main-content">
      <header className="cart-page__header">
        <p className="cart-page__eyebrow">Bag / {itemCount} items</p>
        <h1>Your shopping bag</h1>
      </header>

      <div className="cart-page__layout">
        <section className="cart-page__products" aria-label="Products in bag">
          <div className="cart-page__products-heading" aria-hidden="true">
            <span>Product</span>
            <span>Total</span>
          </div>

          <div className="cart-page__items">
            {resolvedItems.map((item) => {
              const { product } = item;
              const image = product.image || product.images?.[0];
              const lineTotal = Number(product.price) * item.quantity;

              return (
                <article
                  className="cart-page__item"
                  key={`${item.id}-${item.size || "one"}-${item.color || "default"}`}
                >
                  <Link
                    className="cart-page__item-image"
                    to={`/shopDetails/${product.id}`}
                    onClick={scrollToTop}
                    aria-label={`View ${product.name}`}
                  >
                    <img
                      src={image}
                      alt={product.name}
                      loading="lazy"
                      decoding="async"
                    />
                  </Link>

                  <div className="cart-page__item-content">
                    <div className="cart-page__item-copy">
                      <p className="cart-page__item-category">
                        {product.category}
                      </p>
                      <h2>
                        <Link
                          to={`/shopDetails/${product.id}`}
                          onClick={scrollToTop}
                        >
                          {product.name}
                        </Link>
                      </h2>
                      <dl className="cart-page__item-options">
                        <div>
                          <dt>Color</dt>
                          <dd>{item.color || product.colors?.[0] || "Selected"}</dd>
                        </div>
                        <div>
                          <dt>Size</dt>
                          <dd>{item.size || "One size"}</dd>
                        </div>
                      </dl>
                      <p className="cart-page__unit-price">
                        {formatMoney(Number(product.price))} each
                      </p>
                    </div>

                    <div className="cart-page__item-controls">
                      <div
                        className="cart-page__quantity"
                        aria-label={`Quantity for ${product.name}`}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.size || "",
                              item.color || "",
                              item.quantity - 1
                            )
                          }
                          disabled={item.quantity <= 1}
                          aria-label={`Decrease ${product.name} quantity`}
                        >
                          <span aria-hidden="true">−</span>
                        </button>
                        <span aria-live="polite">{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.size || "",
                              item.color || "",
                              item.quantity + 1
                            )
                          }
                          aria-label={`Increase ${product.name} quantity`}
                        >
                          <span aria-hidden="true">+</span>
                        </button>
                      </div>
                      <button
                        type="button"
                        className="cart-page__remove"
                        onClick={() =>
                          removeFromCart(
                            item.id,
                            item.size || "",
                            item.color || ""
                          )
                        }
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <p className="cart-page__line-total">
                    {formatMoney(lineTotal)}
                  </p>
                </article>
              );
            })}
          </div>

          <Link
            to="/shop"
            onClick={scrollToTop}
            className="cart-page__continue-link"
          >
            <span aria-hidden="true">←</span> Continue shopping
          </Link>
        </section>

        <aside className="cart-page__summary" aria-labelledby="cart-summary-heading">
          <div className="cart-page__summary-inner">
            <h2 id="cart-summary-heading">Order summary</h2>

            {FREE_SHIPPING_THRESHOLD > 0 && (
              <div className="cart-page__shipping-progress">
                <p>
                  {hasFreeShipping
                    ? "You unlocked free shipping."
                    : `${formatMoney(remainingForFreeShipping)} away from free shipping.`}
                </p>
                <div
                  className="cart-page__progress-track"
                  role="progressbar"
                  aria-label="Free shipping progress"
                  aria-valuemin="0"
                  aria-valuemax={FREE_SHIPPING_THRESHOLD}
                  aria-valuenow={Math.min(
                    discountedSubtotal,
                    FREE_SHIPPING_THRESHOLD
                  )}
                >
                  <span style={{ width: `${shippingProgress}%` }} />
                </div>
              </div>
            )}

            <form
              className="cart-page__discount"
              onSubmit={handleDiscountSubmit}
              noValidate
            >
              <label htmlFor="cart-discount">Discount code</label>
              <div>
                <input
                  id="cart-discount"
                  value={discountInput}
                  onChange={(event) => {
                    setDiscountInput(event.target.value);
                    setDiscountMessage("");
                  }}
                  placeholder={PROMO_CODE}
                  autoComplete="off"
                  disabled={Boolean(appliedDiscount)}
                />
                {appliedDiscount ? (
                  <button type="button" onClick={removeDiscount}>
                    Remove
                  </button>
                ) : (
                  <button type="submit">Apply</button>
                )}
              </div>
              <p
                className={appliedDiscount ? "is-success" : ""}
                aria-live="polite"
              >
                {discountMessage ||
                  `Use ${PROMO_CODE} for ${Math.round(PROMO_RATE * 100)}% off.`}
              </p>
            </form>

            <dl className="cart-page__totals">
              <div>
                <dt>Subtotal</dt>
                <dd>{formatMoney(subtotal)}</dd>
              </div>
              {discount > 0 && (
                <div className="cart-page__discount-row">
                  <dt>Discount ({PROMO_CODE})</dt>
                  <dd>−{formatMoney(discount)}</dd>
                </div>
              )}
              <div>
                <dt>Shipping to United States</dt>
                <dd>{shipping === 0 ? "Free" : formatMoney(shipping)}</dd>
              </div>
              <div className="cart-page__grand-total">
                <dt>Total</dt>
                <dd>{formatMoney(total)}</dd>
              </div>
            </dl>

            <p className="cart-page__tax-note">
              Taxes included where applicable. Delivery in 5–10 business days.
            </p>
            <button
              type="button"
              className="cart-page__checkout-button"
              onClick={handleCheckout}
            >
              Go to checkout
            </button>
            <p className="cart-page__secure-note">Secure, encrypted checkout</p>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
